import type { PropsWithChildren } from "react";
import { cn } from "./cn";
import { useI18n } from "../i18n";

export interface SideDrawerProps extends PropsWithChildren {
  /** Which edge the drawer is anchored to / slides from. */
  side: "left" | "right";
  /** Slide-in when true; slid off-screen (still mounted) when false. */
  open: boolean;
  title?: string;
  subtitle?: string;
  testId?: string;
  onClose?: () => void;
}

/**
 * SideDrawer — WO-VRD-30 (Claudio). Full-height page-3 drawer that
 * floats over the Grid at an edge and slides in on demand.
 *
 *   • LEFT  → Parcel Profile dossier (opens when a parcel is clicked).
 *   • RIGHT → Context / Selection (opens when an area is drawn).
 *
 * The mode toggle + the "CONTEXT"/"PARCEL PROFILE" labels now live in
 * the top instrument row (see GridWorkspace); these drawers hold only
 * the resulting info. Closing is presentation-only (slide off / hide);
 * re-drawing or re-clicking re-opens.
 */
export function SideDrawer({
  side,
  open,
  title,
  subtitle,
  testId,
  onClose,
  children,
}: SideDrawerProps) {
  const id = testId ?? "side-drawer";
  const { t } = useI18n();
  const closed =
    side === "right"
      ? "translate-x-[calc(100%+1.25rem)]"
      : "-translate-x-[calc(100%+1.25rem)]";
  return (
    <aside
      data-testid={id}
      data-open={open ? "true" : "false"}
      aria-hidden={!open}
      aria-label={title ?? t("grid.panel.panelAria")}
      className={cn(
        // WO-VRD-31 — floated with a free perimeter margin (top clears the
        // instrument bar; bottom/side gaps) so it reads as a detached panel,
        // not glued to the frame. Rounded + all-round hairline border.
        "absolute top-[64px] bottom-4 z-panel pointer-events-auto",
        side === "right" ? "right-4" : "left-4",
        "w-[356px] max-w-[calc(100vw-32px)]",
        "flex flex-col side-drawer-glass rounded-2xl",
        "border border-border-soft overflow-hidden",
        "transition-transform duration-300 ease-out will-change-transform",
        open ? "translate-x-0" : closed,
      )}
    >
      <header
        className={cn(
          "shrink-0 flex items-center justify-between gap-3",
          "h-12 px-4 border-b border-border-soft",
        )}
      >
        <div className="min-w-0 flex items-center gap-2.5">
          <span className="strip-dot" aria-hidden />
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-text-secondary truncate">
            {title}
          </span>
          {subtitle && (
            <span className="font-mono text-[10px] text-text-muted truncate normal-case tracking-normal">
              {subtitle}
            </span>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            data-testid={`${id}-close`}
            aria-label={t("grid.panel.closePanel")}
            onClick={onClose}
            className={cn(
              "shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md",
              "text-text-muted hover:text-neon-teal transition-colors",
              "focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--spec-cyan)]",
            )}
          >
            <svg
              viewBox="0 0 14 14"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            >
              <line x1="3" y1="3" x2="11" y2="11" />
              <line x1="11" y1="3" x2="3" y2="11" />
            </svg>
          </button>
        )}
      </header>
      <div
        data-testid={`${id}-body`}
        className="min-h-0 flex-1 overflow-y-auto scroll-dark"
      >
        {children}
      </div>
    </aside>
  );
}
